// core/undock.js — pop a live view into its own OS window (Document PiP).
//
// Document Picture-in-Picture (Chromium 116+) hands us a real, always-on-top
// window whose document we fully own — unlike <video> PiP it hosts arbitrary
// DOM. We MOVE the live view node into it (not a clone) so every listener,
// timer and bit of view state survives the trip, copy our stylesheets across
// (the popout document starts blank), and move the node back on close.
//
// One view out at a time. Feature-detected: on browsers without the API
// (Safari, Firefox today) isSupported() is false and the caller hides the
// affordance instead of offering a dead button.

const hasApi = () => 'documentPictureInPicture' in window;

// { viewEl, placeholder, parent, wasHidden, pip }
let active = null;

/** Re-create the host page's CSS inside the popout document. */
function copyStyles(srcDoc, pipDoc) {
  // Carry the theme choice so the popout doesn't flash the wrong palette.
  const theme = srcDoc.documentElement.getAttribute('data-theme');
  if (theme) pipDoc.documentElement.setAttribute('data-theme', theme);
  pipDoc.documentElement.style.colorScheme = theme === 'light' ? 'light' : 'dark';

  for (const sheet of [...srcDoc.styleSheets]) {
    // Linked sheets: clone the <link> (cheap, and dodges the cross-origin
    // cssRules SecurityError entirely).
    if (sheet.href) {
      const link = pipDoc.createElement('link');
      link.rel = 'stylesheet';
      link.href = sheet.href;
      pipDoc.head.appendChild(link);
      continue;
    }
    // Inline / constructed sheets: re-emit their rules (adopted sheets and
    // CSSOM don't cross document boundaries on their own).
    try {
      const style = pipDoc.createElement('style');
      style.textContent = [...sheet.cssRules].map(r => r.cssText).join('\n');
      pipDoc.head.appendChild(style);
    } catch {
      // Opaque cross-origin sheet with no href to re-link — skip it.
    }
  }
}

export function isSupported() {
  return hasApi();
}

export function isUndocked() {
  return !!active;
}

/**
 * Move `viewEl` into a fresh Document-PiP window. Must be called from a user
 * gesture (click). Resolves true on success, false if unsupported / denied /
 * already undocked.
 */
export async function undockView(viewEl, { title } = {}) {
  if (!hasApi() || !viewEl || active) return false;

  const rect = viewEl.getBoundingClientRect();
  let pip;
  try {
    pip = await window.documentPictureInPicture.requestWindow({
      width: Math.max(360, Math.round(rect.width) || 960),
      height: Math.max(240, Math.round(rect.height) || 640),
    });
  } catch {
    return false; // no user gesture, or the user dismissed the prompt
  }

  copyStyles(document, pip.document);
  pip.document.title = title || document.title;
  pip.document.body.classList.add('zap-pip-body');
  // Explicit bg/fg from the live page so there's no white frame before the
  // copied sheet's :root vars resolve.
  const cs = getComputedStyle(document.body);
  pip.document.body.style.background = cs.backgroundColor || '#121212';
  pip.document.body.style.color = cs.color || '#fff';

  // Mark the original slot so the node returns to the exact same position.
  const parent = viewEl.parentNode;
  const placeholder = document.createComment('zap-pip-slot');
  const wasHidden = viewEl.hidden;
  parent.replaceChild(placeholder, viewEl);
  viewEl.hidden = false; // always visible inside its own window
  pip.document.body.appendChild(viewEl);

  const redock = () => {
    if (!active || active.pip !== pip) return;
    active = null;
    viewEl.hidden = wasHidden;
    if (placeholder.parentNode) placeholder.parentNode.replaceChild(viewEl, placeholder);
    else parent.appendChild(viewEl);
    // Let the SPA re-assert correct visibility (the user may have navigated
    // away while this view was popped out).
    document.dispatchEvent(new CustomEvent('zap:redock', { detail: { el: viewEl } }));
  };
  pip.addEventListener('pagehide', redock, { once: true });

  active = { viewEl, placeholder, parent, wasHidden, pip };
  document.dispatchEvent(new CustomEvent('zap:undock', { detail: { el: viewEl } }));
  return true;
}

/** Close the popout (fires pagehide → the node moves back). */
export function redockView() {
  active?.pip?.close();
}

/** Undock if docked, redock if already out. */
export function toggleUndock(viewEl, opts) {
  if (active) { redockView(); return Promise.resolve(false); }
  return undockView(viewEl, opts);
}
